/**
 * InteractionSystem — proximity prompts, auto triggers and exits over a map's interaction data.
 *
 * The owning scene feeds the player's feet position + facing every fixed step (`update`). The system
 *  - picks the best enabled `MapInteraction` in front of (probe point) or under the player,
 *  - emits `interact:prompt` on the bus only when the candidate changes ({ label, kind, id } | null),
 *  - fires `auto` interactions once per entry (edge-triggered),
 *  - detects exits (edge-triggered `onExit`) — the scene performs the actual Router transition,
 *  - gates by `requireFlag` / `requireMark` / `untilFlag` / `once` through story callbacks (`InteractionGate`),
 *  - resolves confirm presses (`tryInteract`) into `onTalk` / `onObserve` / `onTrigger` / `onExit`.
 *
 * Phaser-free (bus is Phaser-independent) so selection logic is unit-tested in Node.
 */
import { bus, Events } from '@/core/events';
import type { Facing } from '@/core/types';
import type { MapExit, MapInteraction } from '@/core/types/contracts';
import { distSqToRectCenter, exitAt as findExit, rectContains, rectsIntersect, type Rect } from '@/maps/queries';

export type InteractionKind = MapInteraction['kind'];

/** Story-state adapter. `hasFlag` must be true when the story index is at or past the flag. */
export interface InteractionGate {
  hasFlag(flag: string): boolean;
  hasMark(mark: string): boolean;
  setMark(mark: string): void;
}

/** Resolution callbacks — the scene launches dialogue / cinematics / Router.go here. */
export interface InteractionHandlers {
  onTalk?(i: MapInteraction): void;
  onObserve?(i: MapInteraction): void;
  onTrigger?(i: MapInteraction): void;
  /** Walking into an exit rect, or confirming a `kind: 'exit'` interaction (then `via` is set). */
  onExit?(exit: MapExit | null, via?: MapInteraction): void;
}

export interface InteractionSource { interactions: readonly MapInteraction[]; exits: readonly MapExit[] }

export interface InteractPromptPayload { label?: string; kind: InteractionKind; id: string }

export interface InteractionSystemOptions {
  /**
   * Distance (px) from the FEET baseline to the probe point in the facing direction (default 26).
   * Measured from the feet for every facing so up/down reach is symmetric; with the default 6 px
   * half-box the probe covers the whole adjacent tile from anywhere inside the player's tile and
   * never touches the tile beyond it.
   */
  reach?: number;
  /** Half size of the forgiving probe box around the probe point (default 6 → a 13 × 13 px box centred on the probe pixel). */
  probeHalf?: number;
  /** Body centre sits this many px above the feet baseline (used for "under the body" and auto-zone checks). */
  bodyCenterOffsetY?: number;
  /** Replace the bus emitter (tests). */
  emitPrompt?: (payload: InteractPromptPayload | null) => void;
  labels?: Partial<Record<InteractionKind, string>>;
}

export const DEFAULT_LABELS: Record<InteractionKind, string> = { talk: '交谈', observe: '查看', trigger: '触发', exit: '离开' };

export function facingDelta(f: Facing): { dx: number; dy: number } {
  switch (f) {
    case 'up': return { dx: 0, dy: -1 };
    case 'down': return { dx: 0, dy: 1 };
    case 'left': return { dx: -1, dy: 0 };
    case 'right': return { dx: 1, dy: 0 };
  }
}

export function doneMark(id: string): string { return `done:${id}`; }

/** Adapter for the project StoryState (structural, so no import of another builder's class). */
export function storyGate(story: { has(flag: never): boolean; hasMark(id: string): boolean; mark(id: string): boolean }, isFlag: (f: string) => boolean): InteractionGate {
  return {
    hasFlag: (f) => isFlag(f) && story.has(f as never),
    hasMark: (m) => story.hasMark(m),
    setMark: (m) => { story.mark(m); },
  };
}

export class InteractionSystem {
  private readonly reach: number;
  private readonly probeHalf: number;
  private readonly offY: number;
  private readonly emit: (payload: InteractPromptPayload | null) => void;
  private readonly labels: Record<InteractionKind, string>;
  private handlers: InteractionHandlers;
  private candidate: MapInteraction | null = null;
  private promptId: string | null = null;
  private readonly insideAuto = new Set<string>();
  private insideExit: string | null = null;
  private locked = false;
  private destroyed = false;
  private x = 0; private y = 0; private facing: Facing = 'down';

  constructor(private readonly source: InteractionSource, private readonly gate: InteractionGate, handlers: InteractionHandlers = {}, opts: InteractionSystemOptions = {}) {
    this.handlers = handlers;
    this.reach = opts.reach ?? 26;
    this.probeHalf = opts.probeHalf ?? 6;
    this.offY = opts.bodyCenterOffsetY ?? 6;
    this.emit = opts.emitPrompt ?? ((p) => { bus.emitTyped(Events.INTERACT_PROMPT, p); });
    this.labels = { ...DEFAULT_LABELS, ...(opts.labels ?? {}) };
  }

  setHandlers(h: InteractionHandlers): void { this.handlers = h; }

  /** Locked during dialogue / cinematics: no prompt, no auto triggers, no exits, confirm ignored. */
  setLocked(on: boolean): void {
    this.locked = on;
    if (on) this.setCandidate(null);
  }
  isLocked(): boolean { return this.locked; }

  /** Gating: requireFlag reached, requireMark set, untilFlag not reached, and not already done when `once`. */
  isEnabled(i: MapInteraction): boolean {
    if (i.requireFlag && !this.gate.hasFlag(i.requireFlag)) return false;
    if (i.requireMark && !this.gate.hasMark(i.requireMark)) return false;
    if (i.untilFlag && this.gate.hasFlag(i.untilFlag)) return false;
    if (i.once && this.gate.hasMark(doneMark(i.id))) return false;
    return true;
  }

  /** Current best non-auto interaction (the one a confirm press would use). */
  current(): MapInteraction | null { return this.candidate; }

  /** Last emitted prompt payload (null when hidden). */
  prompt(): InteractPromptPayload | null {
    return this.candidate ? this.payloadFor(this.candidate) : null;
  }

  /** Where the facing probe currently is: `reach` px from the feet in the facing direction (debug overlays). */
  probePoint(): { x: number; y: number } {
    const d = facingDelta(this.facing);
    return { x: this.x + d.dx * this.reach, y: this.y + d.dy * this.reach };
  }

  /** The forgiving box around the probe point (odd-sized so it is pixel-symmetric about the probe). */
  probeBox(): Rect {
    const p = this.probePoint();
    return { x: p.x - this.probeHalf, y: p.y - this.probeHalf, w: this.probeHalf * 2 + 1, h: this.probeHalf * 2 + 1 };
  }

  /** Call once per fixed step with the player's feet position and facing. */
  update(x: number, y: number, facing: Facing): void {
    if (this.destroyed) return;
    this.x = x; this.y = y; this.facing = facing;
    if (this.locked) { this.setCandidate(null); return; }

    // exits: edge-triggered on entering the rect; re-armed after leaving it
    const exit = findExit(this.source.exits, x, y);
    if (exit) {
      if (this.insideExit !== exit.id) { this.insideExit = exit.id; this.handlers.onExit?.(exit); if (this.destroyed || this.locked) return; }
    } else this.insideExit = null;

    // auto interactions: fire once per entry of the body centre; `once` ones are gated via marks
    const cx = x, cy = y - this.offY;
    for (const i of this.source.interactions) {
      if (!i.auto) continue;
      const inside = rectContains(i, cx, cy) || rectContains(i, x, y);
      if (inside && this.isEnabled(i)) {
        if (!this.insideAuto.has(i.id)) { this.insideAuto.add(i.id); this.fire(i); if (this.destroyed || this.locked) return; }
      } else if (!inside) this.insideAuto.delete(i.id);
    }

    this.setCandidate(this.select());
  }

  /** Confirm pressed: resolves the current candidate through the handlers. */
  tryInteract(): MapInteraction | null {
    if (this.destroyed || this.locked) return null;
    const c = this.candidate;
    if (!c || !this.isEnabled(c)) { this.setCandidate(this.select()); return null; }
    this.fire(c);
    if (!this.destroyed && !this.locked) this.setCandidate(this.select());
    return c;
  }

  /** Exit rect containing the point (pass-through for scenes that resolve exits themselves). */
  exitAt(px: number, py: number): MapExit | null { return findExit(this.source.exits, px, py); }

  destroy(): void {
    if (this.destroyed) return;
    this.setCandidate(null);
    this.destroyed = true;
    this.handlers = {};
  }

  // ---------------------------------------------------------------- internals

  private payloadFor(i: MapInteraction): InteractPromptPayload {
    return { label: i.label ?? this.labels[i.kind], kind: i.kind, id: i.id };
  }

  private setCandidate(c: MapInteraction | null): void {
    this.candidate = c;
    const id = c?.id ?? null;
    if (id === this.promptId) return;
    this.promptId = id;
    this.emit(c ? this.payloadFor(c) : null);
  }

  /** Best enabled non-auto interaction: in front (probe point > probe box) beats under the body. */
  private select(): MapInteraction | null {
    const cx = this.x, cy = this.y - this.offY;
    const { x: px, y: py } = this.probePoint();
    const probeBox = this.probeBox();
    let best: MapInteraction | null = null;
    let bestRank = Infinity, bestDist = Infinity;
    for (const i of this.source.interactions) {
      if (i.auto || !this.isEnabled(i)) continue;
      let rank: number;
      if (rectContains(i, px, py)) rank = 0;
      else if (rectsIntersect(i, probeBox)) rank = 1;
      else if (rectContains(i, cx, cy) || rectContains(i, this.x, this.y)) rank = 2;
      else continue;
      const dist = distSqToRectCenter(i, cx, cy);
      if (rank < bestRank || (rank === bestRank && dist < bestDist)) { best = i; bestRank = rank; bestDist = dist; }
    }
    return best;
  }

  private fire(i: MapInteraction): void {
    switch (i.kind) {
      case 'talk': this.handlers.onTalk?.(i); break;
      case 'observe': this.handlers.onObserve?.(i); break;
      case 'trigger': this.handlers.onTrigger?.(i); break;
      case 'exit': {
        const exit = this.source.exits.find((e) => e.id === i.id) ?? this.source.exits.find((e) => rectsIntersect(e, i)) ?? null;
        if (!exit) console.warn(`[interaction] exit interaction "${i.id}" matches no exit rect`);
        this.handlers.onExit?.(exit, i);
        break;
      }
    }
    if (i.once) this.gate.setMark(doneMark(i.id));
  }
}
